import React, { useState } from "react";
import type { ManuscriptChangeMessageRef } from "./manuscriptChangeMessageRef";

type Props = {
  change: ManuscriptChangeMessageRef;
  wholeScope?: boolean;
  allowApply: boolean;
  allowUndo: boolean;
  onApply: () => void;
  onUndo: () => Promise<boolean>;
};

/** Fixed substitutions are an editing receipt, not a new prose suggestion. */
export function LiteralReplacementResult({ change, wholeScope = false, allowApply, allowUndo, onApply, onUndo }: Props) {
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState(false);
  const pairs = change.preview?.literalReplacements ?? [];
  const count = change.preview?.editCount ?? 0;
  const occurrences = `${count} ${count === 1 ? "occurrence" : "occurrences"}`;
  const removed = pairs.every((pair) => pair.replacementText === "");
  const verb = removed ? "Remove" : "Replace";
  const summary = change.status === "undone" ? "Replacements undone."
    : change.status === "partially_undone" ? "Some replacements were undone; others remain applied."
    : change.status === "undoing" ? "Undoing replacements…"
    : change.status === "partially_applied" ? "Some replacements were applied."
    : change.status === "applying" ? "Applying replacements…"
    : change.status === "applied" || change.status === "visible"
      ? `${removed ? "Removed" : "Replaced"} ${occurrences}.`
    : change.status === "declined" ? "Replacements declined."
    : change.status === "superseded" ? "Replacements superseded."
    : ["failed", "could_not_apply", "conflict"].includes(change.status) ? "Replacements could not be applied."
    : change.status === "needs_review_again" ? "The document changed. Review the replacements again."
    : `${verb} ${occurrences}.`;
  const wholeScopeSummary = change.status === "generating" ? "Preparing manuscript edit…"
    : change.status === "undone" ? "Manuscript edit undone."
    : change.status === "partially_undone" ? "Some manuscript edits were undone."
    : change.status === "undoing" ? "Undoing manuscript edit…"
    : change.status === "partially_applied" ? "Some manuscript edits were applied."
    : change.status === "applying" ? "Applying manuscript edit…"
    : change.status === "applied" || change.status === "visible" ? "Manuscript edit applied."
    : change.status === "preview_only" ? "Manuscript revision prepared for review."
    : change.status === "declined" ? "Manuscript edit declined."
    : change.status === "superseded" ? "Manuscript edit superseded."
    : ["failed", "could_not_apply", "could_not_generate", "conflict"].includes(change.status) ? "Manuscript edit could not be completed."
    : change.status === "needs_review_again" ? "The manuscript changed. Review the edit again."
    : "Manuscript edit not yet applied.";
  return (
    <div className="flex flex-col gap-2 text-left text-base text-app-grey-0" aria-label={wholeScope ? "Manuscript edit result" : "Find and replace result"}>
      <p className="m-0" role="status">{wholeScope ? wholeScopeSummary : summary}</p>
      {!wholeScope ? <ul className="m-0 list-none p-0 space-y-1">
        {pairs.map((pair, index) => (
          <li key={index} className="break-words whitespace-pre-wrap">
            <span>“{pair.searchText}”</span>{" → "}
            <span>{pair.replacementText === "" ? "(remove)" : `“${pair.replacementText}”`}</span>
          </li>
        ))}
      </ul> : null}
      {undoError ? <p role="alert" className="m-0">Couldn’t undo the edit. Please try again.</p> : null}
      {(!wholeScope && allowApply) || allowUndo ? (
        <div className="flex gap-3">
          {!wholeScope && allowApply ? <button type="button" className="border-0 bg-transparent p-0 font-inherit text-app-primary-cta underline cursor-pointer disabled:opacity-50" aria-label={wholeScope ? "Apply manuscript edit" : "Apply replacements"} onClick={onApply}>Apply</button> : null}
          {allowUndo ? <button type="button" className="border-0 bg-transparent p-0 font-inherit text-app-primary-cta underline cursor-pointer disabled:opacity-50" aria-label={wholeScope ? "Undo manuscript edit" : "Undo replacements"} disabled={undoing} onClick={async () => {
            setUndoing(true);
            setUndoError(false);
            try { await onUndo(); } catch { setUndoError(true); } finally { setUndoing(false); }
          }}>{undoing ? "Undoing…" : "Undo"}</button> : null}
        </div>
      ) : null}
    </div>
  );
}
